import React, { useState, useEffect } from 'react';
import { Printer, Plus, Trash2, CheckCircle, XCircle, RefreshCcw, Activity, MapPin } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '/api';

const YaziciYonetimi = () => {
  const { token } = useAuth();
  const [yazicilar, setYazicilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [testing, setTesting] = useState({});

  useEffect(() => {
    fetchYazicilar();
  }, []);

  const fetchYazicilar = async () => {
    try {
      const r = await fetch(`${API}/yazicilar`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.ok) setYazicilar(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    const method = modal.yaziciID ? 'PUT' : 'POST';
    const url = modal.yaziciID ? `${API}/yazicilar/${modal.yaziciID}` : `${API}/yazicilar`;

    const body = { ...modal };
    if (body.tryCodePage !== undefined && body.tryCodePage !== null && body.tryCodePage !== '') {
      body.codePage = body.tryCodePage;
    }
    delete body.tryCodePage;
    delete body.customEncoding;
    delete body.customCodePage;

    try {
      const r = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      if (r.ok) {
        setModal(null);
        fetchYazicilar();
      }
    } catch (e) { console.error(e); }
  };

  const remove = async (id) => {
    if (!window.confirm('Bu yazıcıyı silmek istediğinize emin misiniz?')) return;
    try {
      const r = await fetch(`${API}/yazicilar/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.ok) fetchYazicilar();
    } catch (e) { console.error(e); }
  };

  const testConnection = async (id) => {
    setTesting(p => ({ ...p, [id]: true }));
    const yazici = yazicilar.find(y => y.yaziciID === id);
    const payload = { ip: yazici?.ipAdresi, port: yazici?.port };
    console.log('Test payload:', JSON.stringify(payload));
    try {
      const r = await fetch(`${API}/yazicilar/${id}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await r.json();
      if (r.ok) {
        if (data.jobId) {
          alert('⏳ ' + data.mesaj);
        } else {
          alert('✅ ' + (data.mesaj || 'Bağlantı Başarılı!'));
        }
        console.log('Backend response:', data);
      } else {
        alert('❌ Hata: ' + (data.mesaj || 'Bağlanılamadı'));
        console.error('Backend error response:', data);
      }
    } catch (e) {
      console.error('Network error:', e);
      alert('❌ Bağlantı hatası: ' + (e.message || 'Bilinmeyen hata'));
    }
    finally { setTesting(p => ({ ...p, [id]: false })); }
  };

  if (loading) return <div className="p-8 text-center">Yükleniyor...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-surface-800 dark:text-white">Yazıcı Yönetimi</h2>
          <p className="text-sm text-surface-500">Mutfak, Bar ve Kasa yazıcılarını yapılandırın.</p>
        </div>
        <button
          onClick={() => setModal({ yaziciAdi: '', ipAdresi: '192.168.1.', port: 9100, yaziciTuru: 'Mutfak', bolum: 'MUTFAK', aktif: true, encoding: 1254, codePage: 17 })}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors font-medium shadow-lg shadow-emerald-500/20"
        >
          <Plus size={18} /> Yeni Yazıcı Ekle
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {yazicilar.map(y => (
          <div key={y.yaziciID} className="bg-white dark:bg-surface-800 rounded-2xl p-5 border border-surface-100 dark:border-surface-700 shadow-soft hover:shadow-soft-lg transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                  y.bolum === 'MUTFAK' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-500' :
                  y.bolum === 'BAR' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-500' :
                  'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500'
                }`}>
                  <Printer size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-surface-800 dark:text-white">{y.yaziciAdi}</h3>
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-surface-100 dark:bg-surface-700 rounded-full text-surface-500 uppercase">
                    {y.bolum}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setModal(y)} className="p-2 text-surface-400 hover:text-blue-500 transition-colors">
                  <Activity size={18} />
                </button>
                <button onClick={() => remove(y.yaziciID)} className="p-2 text-surface-400 hover:text-rose-500 transition-colors">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-surface-400">IP Adresi:</span>
                <span className="font-mono font-medium text-surface-700 dark:text-surface-300">{y.ipAdresi}:{y.port}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-surface-400">Encoding:</span>
                <span className="font-mono font-medium text-surface-700 dark:text-surface-300">
                  CP{y.encoding || 1254} / ESC t {y.codePage || 17}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-surface-400">Durum:</span>
                <span className={`flex items-center gap-1 font-medium ${y.aktif ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {y.aktif ? <CheckCircle size={12} /> : <XCircle size={12} />}
                  {y.aktif ? 'Aktif' : 'Pasif'}
                </span>
              </div>
            </div>

            <button
              onClick={() => testConnection(y.yaziciID)}
              disabled={testing[y.yaziciID]}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-surface-50 dark:bg-surface-700/50 hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-600 dark:text-surface-400 rounded-xl transition-all text-sm font-semibold border border-surface-100 dark:border-surface-600"
            >
              {testing[y.yaziciID] ? <RefreshCcw size={16} className="animate-spin" /> : <Activity size={16} />}
              Bağlantı Testi
            </button>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={() => setModal(null)}>
          <div className="bg-white dark:bg-surface-800 rounded-3xl w-full max-w-md shadow-soft-xl animate-scale-in" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-surface-100 dark:border-surface-700">
              <h2 className="text-xl font-bold text-surface-800 dark:text-white">
                {modal.yaziciID ? 'Yazıcıyı Düzenle' : 'Yeni Yazıcı Ekle'}
              </h2>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-surface-400 uppercase mb-1">Yazıcı Adı</label>
                <input
                  value={modal.yaziciAdi}
                  onChange={e => setModal(p => ({ ...p, yaziciAdi: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-surface-50 dark:bg-surface-700 border border-surface-100 dark:border-surface-600 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:outline-none text-sm"
                  placeholder="örn: Mutfak Yazıcısı"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-surface-400 uppercase mb-1">IP Adresi</label>
                  <input
                    value={modal.ipAdresi}
                    onChange={e => setModal(p => ({ ...p, ipAdresi: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-surface-50 dark:bg-surface-700 border border-surface-100 dark:border-surface-600 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:outline-none text-sm font-mono"
                    placeholder="192.168.1.50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-surface-400 uppercase mb-1">Port</label>
                  <input
                    type="number"
                    value={modal.port}
                    onChange={e => setModal(p => ({ ...p, port: parseInt(e.target.value) }))}
                    className="w-full px-4 py-2.5 bg-surface-50 dark:bg-surface-700 border border-surface-100 dark:border-surface-600 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:outline-none text-sm"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-surface-400 uppercase mb-1">Bölüm</label>
                  <select
                    value={modal.bolum}
                    onChange={e => setModal(p => ({ ...p, bolum: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-surface-50 dark:bg-surface-700 border border-surface-100 dark:border-surface-600 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:outline-none text-sm"
                  >
                    <option value="MUTFAK">MUTFAK</option>
                    <option value="BAR">BAR</option>
                    <option value="KASA">KASA</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-surface-400 uppercase mb-1">Yazıcı Türü</label>
                  <select
                    value={modal.yaziciTuru}
                    onChange={e => setModal(p => ({ ...p, yaziciTuru: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-surface-50 dark:bg-surface-700 border border-surface-100 dark:border-surface-600 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:outline-none text-sm"
                  >
                    <option value="Mutfak">Mutfak</option>
                    <option value="Adisyon">Adisyon</option>
                    <option value="Bar">Bar</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-surface-400 uppercase mb-1">Encoding / Code Page</label>
                  <select
                    value={`${modal.encoding || 1254}-${modal.codePage || 17}`}
                    onChange={e => {
                      const [enc, cp] = e.target.value.split('-').map(Number);
                      setModal(p => ({ ...p, encoding: enc, codePage: cp, tryCodePage: undefined }));
                    }}
                    className="w-full px-4 py-2.5 bg-surface-50 dark:bg-surface-700 border border-surface-100 dark:border-surface-600 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:outline-none text-sm"
                  >
                    <option value="1254-17">Windows-1254 / ESC t 17</option>
                    <option value="857-12">IBM857 / ESC t 12</option>
                    <option value="857-8">IBM857 / ESC t 8 (Clone)</option>
                    <option value="857-7">IBM857 / ESC t 7 (Clone)</option>
                    <option value="857-6">IBM857 / ESC t 6 (Clone)</option>
                    <option value="857-5">IBM857 / ESC t 5 (Clone)</option>
                    <option value="857-3">IBM857 / ESC t 3 (Clone)</option>
                    <option value="28599-15">ISO-8859-9 / ESC t 15</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-surface-400 uppercase mb-1">ESC t Dene (0-255)</label>
                  <input
                    type="number"
                    min="0" max="255"
                    placeholder={modal.codePage ?? 17}
                    value={modal.tryCodePage ?? ''}
                    onChange={e => {
                      const v = e.target.value;
                      setModal(p => ({ ...p, tryCodePage: v === '' ? undefined : parseInt(v) }));
                    }}
                    className="w-full px-4 py-2.5 bg-surface-50 dark:bg-surface-700 border border-surface-100 dark:border-surface-600 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:outline-none text-sm"
                  />
                  <p className="text-[10px] text-surface-400 mt-1">Boş bırakılırsa dropdown'daki değer kullanılır</p>
                </div>
              </div>
              <div className="flex items-center gap-2 py-2">
                <input
                  type="checkbox"
                  checked={modal.aktif}
                  onChange={e => setModal(p => ({ ...p, aktif: e.target.checked }))}
                  className="w-4 h-4 text-emerald-500 rounded"
                />
                <label className="text-sm font-medium text-surface-700 dark:text-surface-300">Yazıcı Aktif</label>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setModal(null)} className="flex-1 py-2.5 bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300 rounded-xl font-bold text-sm">Vazgeç</button>
                <button type="submit" className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl font-bold text-sm">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default YaziciYonetimi;
